import { Divider } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import ResizeableDrawer from '../../General/ResizeableDrawer/ResizeableDrawer';
import Sidebar from '../../General/Sidebar/Sidebar';
import ProjectViewTree from './ProjectViewTree';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import { leftSidepanelMinWidth, sidepanelMaxWidth } from '../../../Constants';
import { SidepanelVisibilityEnum } from '../../../redux/user/sliceUserSettings';

/**
 * Component that shows the sidebar for the Project View, and handles it's state.
 */
export default () => {
    const sidepanelVisibility = useSelector(
        (store: IAppReduxStore) => store.userSettings.data.sidepanelVisibility,
    );

    /** State slice for controlling the sidebar's open state. */
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);

    /** State slice that tracks which nodes in the TreeView are expanded. */
    const [expanded, setExpanded] = useState<string[]>([]);
    const [isExpandedAll, setIsExpandedAll] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');

    /** Access relevant state variables from the redux store. */
    const structure = useSelector((store: IAppReduxStore) => store.projectViewData.structure.data);

    const getAllCategoriesForOpening = useMemo(() => {
        return Array.from(
            new Set((structure ?? []).reduce((acc, item) => acc.concat(item.category), [])),
        );
    }, [structure]);

    useEffect(() => {
        setSidebarOpen(sidepanelVisibility === SidepanelVisibilityEnum.expanded);
    }, [sidepanelVisibility]);

    const handleToggleAllExpanded = (isExpandAll: boolean) => {
        setExpanded(isExpandAll ? getAllCategoriesForOpening : []);
        setIsExpandedAll(isExpandAll);
    };

    const handleSearchChange = (query: string) => {
        setSearchQuery(query);
    };

    /** Update the expandedNodes state when a node in the TreeView is expanded or collapsed */
    const handleNodeToggle = itemIds => {
        setExpanded(itemIds);
    };

    /** Sidebar component. */
    const sidebar = (
        <Sidebar
            tree={
                <ProjectViewTree
                    expanded={expanded}
                    handleNodeToggle={handleNodeToggle}
                    searchQuery={searchQuery}
                    isExpandedAll={isExpandedAll}
                />
            }
            openState={[sidebarOpen, setSidebarOpen]}
            handleToggleAllExpanded={handleToggleAllExpanded}
            handleSearchChange={handleSearchChange}
            searchQuery={searchQuery}
        />
    );

    if (!sidebarOpen) {
        return (
            <>
                {sidebar}
                <Divider orientation='vertical' flexItem />
            </>
        );
    }

    return (
        <ResizeableDrawer minWidth={leftSidepanelMinWidth} maxWidth={sidepanelMaxWidth}>
            {sidebar}
        </ResizeableDrawer>
    );
};
